import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, RefreshCw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AutosaveStatus } from '../../../common/hooks/use-autosave.hook';

/**
 * What an autosaving page says about its edits, at the top right.
 *
 * Quiet on purpose: nothing until something changes, "Saving…" while it is
 * stored, "Saved" for a few seconds after, and a Retry when a save failed — the
 * one state that needs the person.
 */
export const AutosaveIndicator = ({ status, onRetry }: { status: AutosaveStatus; onRetry: () => void }) => {
    const { t } = useTranslation();
    const [showSaved, setShowSaved] = useState(false);

    useEffect(() => {
        if (status !== 'saved') return;
        setShowSaved(true);
        const timer = window.setTimeout(() => setShowSaved(false), 3000);
        return () => window.clearTimeout(timer);
    }, [status]);

    if (status === 'error') {
        return (
            <span className="flex items-center gap-2 text-xs text-destructive" role="status" aria-live="polite">
                {t('AUTOSAVE.FAILED', 'Couldn’t save')}
                <Button type="button" variant="outline" size="xs" onClick={onRetry}>
                    <RefreshCw className="size-3" />
                    {t('AUTOSAVE.RETRY', 'Retry')}
                </Button>
            </span>
        );
    }

    if (status === 'pending' || status === 'saving') {
        return (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground" role="status" aria-live="polite">
                <Loader2 className="size-3.5 animate-spin" />
                {t('AUTOSAVE.SAVING', 'Saving…')}
            </span>
        );
    }

    return (
        <span
            className={cn(
                'flex items-center gap-1.5 text-xs text-muted-foreground transition-opacity duration-500',
                status === 'saved' && showSaved ? 'opacity-100' : 'opacity-0',
            )}
            role="status"
            aria-live="polite"
        >
            <Check className="size-3.5" />
            {t('AUTOSAVE.SAVED', 'Saved')}
        </span>
    );
};
